import React from 'react'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import UserIcon from 'src/components/ui/UserIcon'
import {
  ISchemaInfo,
  useStorageObjectsStore,
} from 'src/hooks/useStorageObjects'
import { useRouterSmartList } from 'src/hooks/router/useRouterSmartList'
import Skeleton from '@mui/material/Skeleton'
import useResponsive from 'src/hooks/useResponsive'
import { useLayoutContext } from 'src/layouts/VerticalLayout'

export interface ISmartListNavigationProps {
  scheme: ISchemaInfo
  navHover?: boolean
  navVisible?: boolean
}
export const SmartListNavigation = (props: ISmartListNavigationProps) => {
  const { scheme } = props
  const schemeName = scheme.identifier
  const { setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  const { useAction, useSelect } = useStorageObjectsStore({
    prefix: 'primary',
    schemeName,
  })

  const getSmartList = useAction.getSmartList()
  const getSmartListStatus = useAction.getSmartList.status()
  const {
    smartListSelectedId,
    openSmartListEdit,
    // openSmartListNew,
    selectedSmartList,
  } = useRouterSmartList()
  const editButtonId = React.useId()
  const buttonEditId = `edit${editButtonId.replaceAll(':', '_')}`
  const smartListItems = useSelect.smartListItems()
  const smartListNextCursor = useSelect.smartListNextCursor()
  // const isEmpty = getSmartListStatus.isFulfilled && smartListItems.length === 0]

  React.useEffect(() => {
    if (getSmartListStatus.isClear) {
      getSmartList({ perPage: 5 })
    }
  }, [getSmartListStatus.isClear])

  const handleGetSmartListMore = () => {
    if (!getSmartListStatus.isClear) {
      getSmartList({ perPage: 5, cursor: smartListNextCursor as string })
    }
  }

  if (smartListItems.length === 0 && !getSmartListStatus.isPending) {
    return (
      <Stack
        onClick={(event) => event.stopPropagation()}
        sx={{
          borderColor: 'grey.800',
          mb: 0,
          mt: 0,
          fontSize: isMobile ? '1rem' : undefined,
        }}
      >
        <List dense={!isMobile} disablePadding={true}>
          <Stack px={5} alignItems="flex-start">
            <Stack
              direction="row"
              columnGap={3}
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <UserIcon
                icon="material-symbols:filter-list-off"
                fontSize={20}
                color="inherit"
              />
              <Typography
                variant="body2"
                fontSize={12}
                sx={{
                  color: 'grey.500',
                }}
              >
                No smart lists yet
              </Typography>
            </Stack>
          </Stack>
        </List>
      </Stack>
    )
  }

  return (
    <Stack
      onClick={(event) => event.stopPropagation()}
      sx={{
        borderColor: 'grey.800',
        mb: 0,
        mt: 0,
        fontSize: isMobile ? '1rem' : undefined,
      }}
    >
      <List dense={!isMobile} disablePadding={!isMobile}>
        {/* {isEmpty && (
          <Stack height={30} alignItems="start" justifyContent="center">
            <Button
              size="small"
              onClick={() => {
                if (isMobile) {
                  setNavHandle?.()
                }
                openSmartListNew({
                  prefix: 'primary',
                  schemeName,
                })
              }}
            >
              <Typography noWrap color="inherit">
                Create smart list
              </Typography>
            </Button>
          </Stack>
        )} */}

        {getSmartListStatus.isPending ? (
          <SmartListSkeleton />
        ) : (
          smartListItems.map((item) => (
            <ListItemButton
              key={item.id}
              dense={!isMobile}
              onClick={() => {
                if (isMobile) {
                  setNavHandle?.()
                }
                selectedSmartList({
                  prefix: 'primary',
                  schemeName,
                  id: item.id,
                })
              }}
              sx={{
                color:
                  Number(smartListSelectedId) === item.id
                    ? 'primary.main'
                    : 'common.white',

                '&:hover': {
                  color: 'primary.main',
                },
                [`& .${buttonEditId}`]: {
                  opacity: 0,
                },
                [`&:hover .${buttonEditId}`]: {
                  opacity: 1,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit !important' }}>
                <UserIcon
                  color="inherit"
                  icon={
                    item.type === 'manual'
                      ? 'eva:file-text-fill'
                      : 'material-symbols:neurology-outline'
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    sx={{ width: '100%' }}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                    color="inherit"
                    fontSize={12}
                  >
                    {item.title}
                  </Typography>
                }
              />
              <IconButton
                onClick={(event) => {
                  event.stopPropagation()
                  selectedSmartList({
                    prefix: 'primary',
                    schemeName,
                    id: item.id,
                  })
                  openSmartListEdit({
                    prefix: 'primary',
                    schemeName,
                    id: item.id,
                  })
                }}
                sx={{ color: 'common.white' }}
                size={isMobile ? 'medium' : 'small'}
                className={!isMobile ? buttonEditId : undefined}
              >
                <UserIcon icon={'material-symbols:edit'} width={16} />
              </IconButton>
            </ListItemButton>
          ))
        )}
        {smartListNextCursor && (
          <Button
            variant="text"
            onClick={handleGetSmartListMore}
            disabled={getSmartListStatus.isPending}
            sx={{ pl: 8, mt: 1 }}
            size={isMobile ? 'medium' : 'small'}
            fullWidth
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'left',
                color: 'primary.light',
                opacity: getSmartListStatus.isPending ? 0.5 : 1,
              }}
            >
              Show more
            </Typography>
          </Button>
        )}
      </List>
    </Stack>
  )
}

function SmartListSkeleton(props: { count?: number }) {
  const { count = 2 } = props

  return (
    <>
      {new Array(count).fill(null).map((_, index) => (
        <ListItemButton key={index} disabled>
          <ListItemIcon>
            <Skeleton
              width={22}
              height={22}
              variant="rounded"
              sx={{ backgroundColor: 'grey.50' }}
            />
          </ListItemIcon>
          <ListItemText>
            <Skeleton
              width={index % 2 ? 100 : 125}
              variant="text"
              sx={{ backgroundColor: 'grey.50', height: '18px !important' }}
            />
          </ListItemText>
        </ListItemButton>
      ))}
    </>
  )
}
